import { Icon } from '../../../../../../../../components/icon';
import { Button } from '../../../../../../../../components/shadcn/button';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';

interface RecordingDetailsBoxProps {
  isRecording: boolean;
  isPaused: boolean;
  isConnected: boolean;
  caseId: string | null;
  onStartRecording: () => void;
  onPauseRecording: () => void;
  onCompleteRecording: () => void;
}

export function LiveControls({
  isRecording,
  isPaused,
  isConnected,
  caseId,
  onStartRecording,
  onPauseRecording,
  onCompleteRecording,
}: RecordingDetailsBoxProps) {
  const getStatusText = () => {
    if (!isConnected) return 'Disconnected';
    if (isPaused) return 'Paused';
    if (isRecording) return 'Recording';
    return 'Ready';
  };

  const getStatusColor = () => {
    if (!isConnected) return { text: 'text-red-500', bg: 'bg-red-500' };
    if (isPaused) return { text: 'text-amber-500', bg: 'bg-amber-500' };
    if (isRecording) return { text: 'text-green-500', bg: 'bg-green-500' };
    return { text: 'text-gray-500', bg: 'bg-gray-500' };
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className={`mr-1 size-1.5 rounded-full ${getStatusColor().bg}`} />
      <div className={`mr-4 text-sm font-medium text-gray-500`}>{getStatusText()}</div>
      {/* Play/Pause button */}
      {!isRecording || isPaused ? (
        <Button
          onClick={() => {
            trackCustomEvent(TrackingEventsV2.StartRecording);
            onStartRecording();
          }}
          disabled={!isConnected || !caseId}
          size="iconSm"
          className="border-green-300 bg-green-50 text-green-600 hover:border-green-400 hover:bg-green-100 hover:text-green-600"
        >
          <Icon className={`${isPaused ? 'lucide--play' : 'lucide--mic'}`} />
        </Button>
      ) : (
        <Button
          onClick={() => {
            trackCustomEvent(TrackingEventsV2.PauseRecording);
            onPauseRecording();
          }}
          size="iconSm"
          title="Pause Recording"
          className="border-amber-300 bg-amber-50 text-amber-600 hover:border-amber-400 hover:bg-amber-100 hover:text-amber-600"
        >
          <Icon className="lucide--pause" />
        </Button>
      )}

      {/* Stop button - show when recording or paused */}
      {(isRecording || isPaused) && (
        <Button
          onClick={() => {
            trackCustomEvent(TrackingEventsV2.StopRecording);
            onCompleteRecording();
          }}
          size="iconSm"
          className="border-red-300 bg-red-50 text-red-600 hover:border-red-400 hover:bg-red-100 hover:text-red-600"
          title="Stop Recording"
        >
          <Icon className="lucide--square" />
        </Button>
      )}
    </div>
  );
}
